import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse
} from '@mui/material';
import { Delete, Edit, Save, Add, ExpandMore, ExpandLess } from '@mui/icons-material';
import api from '../../services/api';

/**
 * Debounce utility for delaying function execution.
 * @param {Function} callback - Function to debounce.
 * @param {number} delay - Delay in milliseconds.
 * @returns {Function} Debounced function.
 */
const useDebounce = (callback, delay) => {
  const timer = useRef();
  return (...args) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      callback(...args);
    }, delay);
  };
};

/**
 * @typedef {Object} TemplateItem
 * @property {number} id - Item ID.
 * @property {string} description - Item description.
 * @property {number} seq - Sequence number.
 */

/**
 * @typedef {Object} Template
 * @property {number} id - Template ID.
 * @property {string} template_name - Template name.
 * @property {string} description - Template description.
 */

/**
 * Checklist Template Settings page for managing checklist templates and items.
 * @component
 * @returns {JSX.Element} Settings UI for checklist templates.
 */
const CheckListTemplateSettings = () => {
  const [templates, setTemplates] = useState([]);
  const [expandedTemplateId, setExpandedTemplateId] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [editingTemplateName, setEditingTemplateName] = useState('');
  const [editingTemplateDescription, setEditingTemplateDescription] = useState('');
  const [templateItems, setTemplateItems] = useState({});
  const [newItemDesc, setNewItemDesc] = useState('');
  const [newItemSeq, setNewItemSeq] = useState('');
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingItemDesc, setEditingItemDesc] = useState('');
  const [editingItemSeq, setEditingItemSeq] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '', id: null, parentId: null });

  useEffect(() => {
    fetchTemplates();
  }, []);

  /**
   * Fetch all checklist templates.
   * @returns {Promise<void>}
   */
  const fetchTemplates = async () => {
    try {
      const res = await api.get('checklist-template/');
      setTemplates(res.data.payload);
    } catch {
      setTemplates([]);
    }
  };

  /**
   * Fetch items for a specific template.
   * @param {number} templateId - Template ID.
   * @returns {Promise<void>}
   */
  const fetchTemplateItems = async (templateId) => {
    try {
      const res = await api.get(`checklist-template-items/${templateId}`);
      setTemplateItems(prev => ({ ...prev, [templateId]: res.data.payload }));
    } catch {
      setTemplateItems(prev => ({ ...prev, [templateId]: [] }));
    }
  };

  /**
   * Debounced creation of a new template.
   * @param {string} name - Template name.
   * @param {string} description - Template description.
   * @returns {Promise<void>}
   */
  const debouncedCreateTemplate = useDebounce(async (name, description) => {
    if (!name.trim() || !description.trim()) return;
    try {
      const res = await api.post('checklist-template/', {
        template_name: name,
        description
      });
      setTemplates(prev => [...prev, res.data]);
      setTemplateName('');
      setTemplateDescription('');
    } catch {}
  }, 1000);

  /**
   * Handle input for template name/description and trigger creation.
   * @param {object} e - Event.
   * @param {string} type - 'name' or 'desc'.
   */
  const handleTemplateInput = (e, type) => {
    if (type === 'name') {
      setTemplateName(e.target.value);
      if (e.target.value.trim() && templateDescription.trim()) {
        debouncedCreateTemplate(e.target.value, templateDescription);
      }
    } else {
      setTemplateDescription(e.target.value);
      if (templateName.trim() && e.target.value.trim()) {
        debouncedCreateTemplate(templateName, e.target.value);
      }
    }
  };

  /**
   * Expand/collapse template and fetch items if needed.
   * @param {number} templateId
   */
  const handleExpandTemplate = (templateId) => {
    setExpandedTemplateId(expandedTemplateId === templateId ? null : templateId);
    if (expandedTemplateId !== templateId) {
      fetchTemplateItems(templateId);
      setNewItemDesc('');
      setNewItemSeq('');
      setEditingItemId(null);
    }
  };

  /**
   * Start editing a template.
   * @param {Template} template
   */
  const handleEditTemplate = (template) => {
    setEditingTemplateId(template.id);
    setEditingTemplateName(template.template_name);
    setEditingTemplateDescription(template.description);
  };

  /**
   * Save template edits.
   * @returns {Promise<void>}
   */
  const handleSaveTemplateEdit = async () => {
    try {
      await api.patch(`checklist-template/${editingTemplateId}`, {
        template_name: editingTemplateName,
        description: editingTemplateDescription
      });
      setTemplates(prev =>
        prev.map(tpl =>
          tpl.id === editingTemplateId
            ? { ...tpl, template_name: editingTemplateName, description: editingTemplateDescription }
            : tpl
        )
      );
      setEditingTemplateId(null);
    } catch {}
  };

  /**
   * Delete a template.
   * @param {number} id
   * @returns {Promise<void>}
   */
  const handleDeleteTemplate = async (id) => {
    try {
      await api.delete(`checklist-template/${id}`);
      setTemplates(prev => prev.filter(tpl => tpl.id !== id));
      setTemplateItems(prev => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
      if (expandedTemplateId === id) setExpandedTemplateId(null);
    } catch {}
    setDeleteDialog({ open: false, type: '', id: null, parentId: null });
  };

  /**
   * Add a new item to a template.
   * @param {number} templateId
   * @returns {Promise<void>}
   */
  const handleAddItem = async (templateId) => {
    if (!newItemDesc.trim()) return;
    try {
      const res = await api.post('checklist-template-items/', {
        template: templateId,
        description: newItemDesc,
        seq: newItemSeq ? parseInt(newItemSeq) : 0
      });
      setTemplateItems(prev => ({
        ...prev,
        [templateId]: [...(prev[templateId] || []), res.data.payload]
      }));
      setNewItemDesc('');
      setNewItemSeq('');
    } catch {}
  };

  /**
   * Start editing a template item.
   * @param {TemplateItem} item
   */
  const handleEditItem = (item) => {
    setEditingItemId(item.id);
    setEditingItemDesc(item.description);
    setEditingItemSeq(item.seq);
  };

  /**
   * Save template item edits.
   * @param {number} templateId
   * @returns {Promise<void>}
   */
  const handleSaveItemEdit = async (templateId) => {
    try {
      await api.patch(`checklist-template-items/${editingItemId}`, {
        description: editingItemDesc,
        seq: editingItemSeq
      });
      setTemplateItems(prev => ({
        ...prev,
        [templateId]: prev[templateId].map(itm =>
          itm.id === editingItemId
            ? { ...itm, description: editingItemDesc, seq: editingItemSeq }
            : itm
        )
      }));
      setEditingItemId(null);
    } catch {}
  };

  /**
   * Delete a template item.
   * @param {number} templateId
   * @param {number} id
   * @returns {Promise<void>}
   */
  const handleDeleteItem = async (templateId, id) => {
    try {
      await api.delete(`checklist-template-items/${id}`);
      setTemplateItems(prev => ({
        ...prev,
        [templateId]: prev[templateId].filter(itm => itm.id !== id)
      }));
    } catch {}
    setDeleteDialog({ open: false, type: '', id: null, parentId: null });
  };

  return (
    <Box sx={{ maxWidth: '100%', mx: 'auto', mt: 4 }}>
      <Typography variant="h5" gutterBottom align="center">
        Checklist Templates
      </Typography>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Add New Template
        </Typography>
        <Box display="flex" gap={2} mb={2}>
          <TextField
            label="Template Name"
            value={templateName}
            onChange={e => handleTemplateInput(e, 'name')}
            size="small"
            fullWidth
          />
          <TextField
            label="Description"
            value={templateDescription}
            onChange={e => handleTemplateInput(e, 'desc')}
            size="small"
            fullWidth
          />
        </Box>
      </Paper>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Templates
        </Typography>
        <List>
          {templates.map(tpl => (
            <React.Fragment key={tpl.id}>
              <ListItem
                alignItems="flex-start"
                selected={expandedTemplateId === tpl.id}
                sx={{ borderRadius: 1, mb: 1, cursor: 'pointer' }}
                onClick={() => handleExpandTemplate(tpl.id)}
                secondaryAction={
                  editingTemplateId === tpl.id ? null : (
                    <>
                      <IconButton edge="end" onClick={e => { e.stopPropagation(); handleEditTemplate(tpl); }}>
                        <Edit />
                      </IconButton>
                      <IconButton
                        edge="end"
                        color="error"
                        onClick={e => { e.stopPropagation(); setDeleteDialog({ open: true, type: 'template', id: tpl.id }); }}
                      >
                        <Delete />
                      </IconButton>
                      <IconButton edge="end" onClick={e => { e.stopPropagation(); handleExpandTemplate(tpl.id); }}>
                        {expandedTemplateId === tpl.id ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </>
                  )
                }
              >
                {editingTemplateId === tpl.id ? (
                  <Box display="flex" gap={1} width="100%">
                    <TextField
                      value={editingTemplateName}
                      onChange={e => setEditingTemplateName(e.target.value)}
                      size="small"
                      label="Name"
                      fullWidth
                    />
                    <TextField
                      value={editingTemplateDescription}
                      onChange={e => setEditingTemplateDescription(e.target.value)}
                      size="small"
                      label="Description"
                      fullWidth
                    />
                    <IconButton onClick={handleSaveTemplateEdit} color="primary">
                      <Save />
                    </IconButton>
                  </Box>
                ) : (
                  <ListItemText
                    primary={tpl.template_name}
                    secondary={tpl.description}
                  />
                )}
              </ListItem>
              <Collapse in={expandedTemplateId === tpl.id} timeout="auto" unmountOnExit>
                <Box sx={{ pl: 4, pr: 2, pb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Template Items
                  </Typography>
                  <Box display="flex" gap={2} mb={2}>
                    <TextField
                      label="Seq"
                      value={newItemSeq}
                      onChange={e => setNewItemSeq(e.target.value.replace(/\D/, ''))}
                      size="small"
                      sx={{ width: 100 }}
                    />
                    <TextField
                      label="Description"
                      value={newItemDesc}
                      onChange={e => setNewItemDesc(e.target.value)}
                      size="small"
                      fullWidth
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<Add />}
                      onClick={() => handleAddItem(tpl.id)}
                      sx={{ minWidth: 120 }}
                    >
                      Add Item
                    </Button>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <List>
                    {(templateItems[tpl.id] || []).map(item => (
                      <ListItem
                        key={item.id}
                        sx={{ borderRadius: 1, mb: 1 }}
                        secondaryAction={
                          editingItemId === item.id ? null : (
                            <>
                              <IconButton edge="end" onClick={() => handleEditItem(item)}>
                                <Edit />
                              </IconButton>
                              <IconButton
                                edge="end"
                                color="error"
                                onClick={() => setDeleteDialog({ open: true, type: 'item', id: item.id, parentId: tpl.id })}
                              >
                                <Delete />
                              </IconButton>
                            </>
                          )
                        }
                      >
                        {editingItemId === item.id ? (
                          <Box display="flex" gap={1} width="100%">
                            <TextField
                              value={editingItemSeq}
                              onChange={e => setEditingItemSeq(e.target.value.replace(/\D/, ''))}
                              size="small"
                              label="Seq"
                              sx={{ width: 100 }}
                            />
                            <TextField
                              value={editingItemDesc}
                              onChange={e => setEditingItemDesc(e.target.value)}
                              size="small"
                              label="Description"
                              fullWidth
                            />
                            <IconButton onClick={() => handleSaveItemEdit(tpl.id)} color="primary">
                              <Save />
                            </IconButton>
                          </Box>
                        ) : (
                          <ListItemText primary={`[${item.seq}] ${item.description}`} />
                        )}
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Collapse>
            </React.Fragment>
          ))}
        </List>
      </Paper>
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, type: '', id: null, parentId: null })}
      >
        <DialogTitle>
          {deleteDialog.type === 'template'
            ? 'Delete Template?'
            : 'Delete Template Item?'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this {deleteDialog.type === 'template' ? 'template' : 'item'}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, type: '', id: null, parentId: null })}>
            Cancel
          </Button>
          <Button
            color="error"
            onClick={() =>
              deleteDialog.type === 'template'
                ? handleDeleteTemplate(deleteDialog.id)
                : handleDeleteItem(deleteDialog.parentId, deleteDialog.id)
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CheckListTemplateSettings;